/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * AcceptedToast — the confirmation pill shown once ALL AI changes in the Post
 * Stream Review are accepted (aggregate accept, accepting the last change, or
 * click-away). It renders inside the same `ModalRegion` as the toolbar, so it keeps
 * the Review moment's vertical position (its `top`); because it is much narrower
 * than the toolbar it is centered horizontally within that container. It fades in,
 * holds, then fades out and calls `onDone` (which converts the review to headless).
 *
 * The word "Accepted" carries a CSS-only shimmer (an animated gradient clipped to
 * the text via `background-clip: text`), built from scratch rather than pulling in
 * a third-party helper. Both the shimmer and the fade respect
 * `prefers-reduced-motion`.
 *
 * `css`/`keyframes` are co-located with the JSX here because `@compiled/react`'s
 * build-time transform requires it (no cross-file styles module).
 */
import { useEffect, useRef, useState } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { renderAcceptedCheckIcon } from './icons';
import { messages } from './messages';

/** How long the pill stays on screen before it fades out and tears down. */
const ACCEPTED_TOAST_DURATION_MS = 2000;
/** Fade-in duration (matches the modal's entrance feel). */
const FADE_IN_MS = 200;
/** Fade-out duration; the teardown fires once it completes. */
const FADE_OUT_MS = 400;
/**
 * Height (px) of the pill — kept equal to the review toolbar's height (the
 * `DEFAULT_REVIEW_MODAL_HEIGHT` fallback in `index.tsx`) so the confirmation keeps
 * the same footprint as the Review moment it replaces.
 */
const REVIEW_TOOLBAR_HEIGHT = 56;

const fadeIn = keyframes({
	from: { opacity: 0 },
	to: { opacity: 1 },
});

const fadeOut = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0 },
});

const shimmer = keyframes({
	from: { backgroundPosition: '200% 0' },
	to: { backgroundPosition: '-200% 0' },
});

// Full-width row so the (narrow) pill centers horizontally within the ModalRegion
// container, which already sits at the Review moment's top.
const rowCSS = css({
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
});

const pillCSS = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.100'),
	height: REVIEW_TOOLBAR_HEIGHT,
	boxSizing: 'border-box',
	paddingRight: token('space.200'),
	paddingLeft: token('space.200'),
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.large', '8px'),
	boxShadow: token('elevation.shadow.overlay'),
	// Purely informational — never intercept clicks, so the author can keep editing.
	pointerEvents: 'none',
	animationName: fadeIn,
	animationDuration: `${FADE_IN_MS}ms`,
	animationTimingFunction: 'ease-out',
	animationFillMode: 'both',
	'@media (prefers-reduced-motion)': {
		animationName: 'none',
	},
});

const pillLeavingCSS = css({
	animationName: fadeOut,
	animationDuration: `${FADE_OUT_MS}ms`,
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	'@media (prefers-reduced-motion)': {
		animationName: 'none',
	},
});

const shimmerTextCSS = css({
	fontWeight: token('font.weight.medium'),
	// The gradient is what the glyphs are painted with (via `background-clip: text`
	// + a transparent text fill); animating its position sweeps a lighter band
	// across the word.
	backgroundImage: `linear-gradient(90deg, ${token('color.text')} 0%, ${token('color.text')} 40%, ${token('color.text.subtlest')} 50%, ${token('color.text')} 60%, ${token('color.text')} 100%)`,
	backgroundSize: '200% 100%',
	WebkitBackgroundClip: 'text',
	backgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	animationName: shimmer,
	animationDuration: '2s',
	animationTimingFunction: 'linear',
	animationIterationCount: 'infinite',
	'@media (prefers-reduced-motion)': {
		animationName: 'none',
		// Solid, readable colour when motion is reduced (no gradient sweep).
		color: token('color.text'),
		WebkitTextFillColor: token('color.text'),
	},
});

/**
 * Renders the "Accepted" confirmation pill. `onDone` fires once the pill has
 * finished its on-screen lifetime (fade-out complete); the caller uses it to
 * convert the Review moment to headless so chat can restore this send.
 */
export function AcceptedToast({ onDone }: { onDone: () => void }): JSX.Element {
	const intl = useIntl();
	const [isLeaving, setIsLeaving] = useState(false);
	// Read `onDone` through a ref so the lifetime timers are set up exactly once
	// (on mount) and never restart if the callback identity changes.
	const onDoneRef = useRef(onDone);
	onDoneRef.current = onDone;

	useEffect(() => {
		const leaveTimer = setTimeout(
			() => setIsLeaving(true),
			ACCEPTED_TOAST_DURATION_MS - FADE_OUT_MS,
		);
		const doneTimer = setTimeout(() => onDoneRef.current(), ACCEPTED_TOAST_DURATION_MS);
		return () => {
			clearTimeout(leaveTimer);
			clearTimeout(doneTimer);
		};
	}, []);

	return (
		<div css={rowCSS}>
			<div
				css={[pillCSS, isLeaving && pillLeavingCSS]}
				role="status"
				aria-live="polite"
				data-testid="post-stream-review-accepted-toast"
			>
				{renderAcceptedCheckIcon()}
				<span css={shimmerTextCSS}>{intl.formatMessage(messages.accepted)}</span>
			</div>
		</div>
	);
}
